import type { KinerjaProfil, KinerjaRoleType } from 'types/kinerja'
import type { KinerjaPsDinasRoleType, PengawasProfile } from 'types/kinerja-ks'

/**
 * Checks if a user has a specific role based on KinerjaProfil.
 * @param data - The user's profile data.
 * @param role - The role to check.
 * @returns Returns true if the user has the specified role, otherwise false.
 */
export const isUserHasRole = (data: KinerjaProfil, role: KinerjaRoleType) => {
  if (!data) return false

  return (
    data?.roles?.some((record) => {
      return record.type === role
    }) || false
  )
}

/**
 * Checks if a PS user has a specific role.
 * @param data - The user's profile data.
 * @param role - The role to check.
 * @returns Returns true if the user has the specified role, otherwise false.
 */
export const isPSUserHasRole = (
  data: PengawasProfile,
  role: KinerjaPsDinasRoleType
) => {
  if (!data) return false

  return (
    data?.roles?.some((item: KinerjaPsDinasRoleType) => {
      return item === role
    }) || false
  )
}

/**
 * Checks if the user has the PS role.
 * @param data - The PengawasProfile data.
 * @returns A boolean indicating whether the user has the PS role.
 */
export const isUserPengawasSekolah = (data: PengawasProfile) => {
  return isPSUserHasRole(data, 'PS')
}

/**
 * Checks if the user has the DINAS role.
 * @param data - The PengawasProfile data.
 * @returns A boolean indicating whether the user has the DINAS role.
 */
export const isUserKepalaDinas = (data: PengawasProfile) => {
  return isPSUserHasRole(data, 'DINAS')
}

/**
 * Checks if the user has PS and DINAS role at the same time.
 * @param data - The PengawasProfile data.
 * @returns A boolean indicating whether the user has PS and DINAS role at the same time.
 */
export const isUserKepalaDinasAndPengawasSekolah = (data: PengawasProfile) => {
  return isUserKepalaDinas(data) && isUserPengawasSekolah(data)
}

export const RoleLabel = {
  DINAS: 'Kepala Dinas Pendidikan',
  PS: 'Pengawas Sekolah',
  TK: 'Tim Kinerja',
  PPK: 'Pejabat Penilai Kinerja',
}

/**
 * Get the Satuan Pendidikan from  KinerjaProfile by NPSN
 * @returns A string of Satuan Pendidikan
 */
export const getSatuanPendidikanByNpsn = (
  profile: KinerjaProfil,
  npsn: string,
  isDemoMode: boolean = false,
  fallback: string = '-'
) => {
  if (isDemoMode) {
    return profile?.satuanPendidikan ?? fallback
  }

  return (
    profile?.roles?.find((role) => role.npsn === npsn)?.satuanPendidikan ??
    fallback
  )
}
